/**
 * Custom Slideshow Component
 * Self-contained carousel for 250 Year Edition Hero Slideshow
 * 
 * Features:
 * - Auto-play with configurable interval
 * - Touch/keyboard navigation
 * - Responsive design (mobile optimized)
 * - Dot pagination
 * - Previous/Next arrow controls
 */

class CustomSlideshow extends HTMLElement {
  constructor() {
    super();
    this.currentSlide = 0;
    this.autoplayInterval = null;
    this.isAutoplay = false;
    this.slides;
    this.dotsContainer;
  }

  connectedCallback() {
    this.initSlideshow();
  }

  disconnectedCallback() {
    this.clearAutoplay();
  }

  initSlideshow() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.prevBtn = this.querySelector('[data-prev]');
    this.nextBtn = this.querySelector('[data-next]');
    this.dotsContainer = this.querySelector('[data-dots]');

    if (!this.slides.length) return;

    // Initialize dots
    this.createDots();

    // Event listeners
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.prevSlide());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.nextSlide());

    // Pause on hover/focus
    this.addEventListener('mouseenter', () => this.pauseAutoplay());
    this.addEventListener('mouseleave', () => this.resumeAutoplay());
    this.addEventListener('focusin', () => this.pauseAutoplay());
    this.addEventListener('focusout', () => this.resumeAutoplay());

    // Keyboard navigation
    document.addEventListener('keydown', (e) => this.handleKeyboard(e));

    // Set initial slide
    this.goToSlide(0);

    // Start autoplay if configured
    const autoplayDelay = this.getAttribute('data-autoplay');
    if (autoplayDelay) {
      this.isAutoplay = true;
      this.startAutoplay(parseInt(autoplayDelay, 10));
    }
  }

  createDots() {
    if (!this.dotsContainer) return;

    this.slides?.forEach((_, index) => {
      const dot = document.createElement('button');
      dot.className = `custom-slideshow__dot ${index === 0 ? 'is-active' : ''}`;
      dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
      dot.setAttribute('type', 'button');
      dot.addEventListener('click', () => {
        this.goToSlide(index);
        this.pauseAutoplay();
      });
      this.dotsContainer?.appendChild(dot);
    });

    this.dots = Array.from(this.dotsContainer?.querySelectorAll('.custom-slideshow__dot'));
  }

  goToSlide(index) {
    if (index < 0 || index >= this.slides?.length) return;

    // Update slides
    this.slides?.forEach((slide, i) => {
      slide.classList.toggle('is-active', i === index);
      slide.setAttribute('aria-hidden', i !== index ? 'true' : 'false');
    });

    // Update dots
    if (this.dots) {
      this.dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === index);
      });
    }

    // Update button states
    this.updateButtonStates();

    this.currentSlide = index;
  }

  nextSlide() {
    const nextIndex = (this.currentSlide + 1) % this.slides?.length;
    this.goToSlide(nextIndex);
  }

  prevSlide() {
    const prevIndex = (this.currentSlide - 1 + this.slides?.length) % this.slides?.length;
    this.goToSlide(prevIndex);
  }

  startAutoplay(interval = 6000) {
    this.clearAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.nextSlide();
    }, interval);
  }

  pauseAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  resumeAutoplay() {
    if (this.isAutoplay && !this.autoplayInterval) {
      const autoplayDelay = this.getAttribute('data-autoplay');
      if (autoplayDelay) {
        this.startAutoplay(parseInt(autoplayDelay, 10));
      }
    }
  }

  clearAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  handleKeyboard(e) {
    if (!this.contains(document.activeElement)) return;

    switch (e.key) {
      case 'ArrowRight':
        e.preventDefault();
        this.nextSlide();
        this.pauseAutoplay();
        break;
      case 'ArrowLeft':
        e.preventDefault();
        this.prevSlide();
        this.pauseAutoplay();
        break;
    }
  }

  updateButtonStates() {
    if (!this.prevBtn || !this.nextBtn) return;

    // For infinite loop, buttons are always enabled
    // If you want to disable at ends, uncomment below:
    // const isFirst = this.currentSlide === 0;
    // const isLast = this.currentSlide === this.slides.length - 1;
    // this.prevBtn.disabled = isFirst;
    // this.nextBtn.disabled = isLast;
  }
}

if (!customElements.get('custom-slideshow')) {
  customElements.define('custom-slideshow', CustomSlideshow);
}
